import React, { useMemo, useState } from 'react';
import { Search, AlertTriangle, Clock, ArrowRight, Users, ArrowUpRight, ArrowDownRight, CheckCircle2, Download, Plus, FileText, GraduationCap, Link2 } from 'lucide-react';
import { MODULES, getModuleIcon } from '../../../content';
import { downloadCsv } from '../../../utils/csvExport';
import { formatRelativeTime } from '../../../utils/formatters';
import { GroupId } from '../../../config/systemConfig';
import { StudentData } from './StudentDetailPanel';
import { PageLayout } from '../../shared/PageLayout';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Select } from '../../../components/ui/Select';
import { Input } from '../../../components/ui/Input';
import { EmptyState } from '../../../components/shared/EmptyState';
import { getAvatarGradient } from '../utils/avatarUtils';
import { needsAttention, getLearnerClassroomFlags } from '../utils/studentUtils';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { type Classroom, type TeacherAccount } from '../../../accounts';
import { getWeeklyActivityForLearner, getLearnerTrendDelta, type DayActivity } from '../../../utils/activityHelpers';
import { DjayNarrator } from '../../../mascot';
import { ClassroomSwitcher } from '../components/ClassroomSwitcher';
import { DataFreshness } from '../../shared/DataFreshness';
import { generateClassroomId, validateNewClassroom } from '../../../accounts';
interface MyClassPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: string) => number;
  onSelectStudent: (studentId: string) => void;
  activeAccount: TeacherAccount;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
  classroomName?: string;
  onNavigate?: (route: string) => void;
  onSelectClassroom?: (id: string | null) => void;
}
type FilterType = 'all' | 'attention' | 'good' | 'inactive';
type SortType = 'name' | 'mastery' | 'activity';
export function MyClassPage({
  students,
  selectedGroup,
  getModuleMastery,
  onSelectStudent,
  activeAccount,
  classrooms = [],
  selectedClassroomId = null,
  classroomName,
  onNavigate,
  onSelectClassroom
}: MyClassPageProps) {
  const {
    accounts
  } = useAppStore();
  const activeClassroom = useMemo(() => classrooms.find(c => c.id === selectedClassroomId) || null, [classrooms, selectedClassroomId]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [activeSort, setActiveSort] = useState<SortType>('name');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastLoadedAt, setLastLoadedAt] = useState(() => new Date());
  // Inline classroom creation state
  const [newClassName, setNewClassName] = useState('');
  const [newGroupLevel, setNewGroupLevel] = useState(6);
  const [classWarnings, setClassWarnings] = useState<any[]>([]);
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);
  const handleAddClassroom = () => {
    if (!newClassName.trim()) return;
    const warnings = validateNewClassroom(accounts, newClassName.trim(), activeAccount.id);
    const hasSameTeacherDuplicate = warnings.some(w => w.details.teacherId === activeAccount.id);
    if (hasSameTeacherDuplicate) {
      setClassWarnings(warnings);
      return;
    }
    if (warnings.length > 0 && !confirmDuplicate) {
      setClassWarnings(warnings);
      return;
    }
    const newClassroom: Classroom = {
      id: generateClassroomId(),
      name: newClassName.trim(),
      groupLevel: newGroupLevel,
      learnerIds: [],
      createdAt: Date.now(),
      teacherId: activeAccount.id,
      joinCode: '' // Auto-generated by addClassroom()
    };
    useAppStore.getState().addClassroom(activeAccount.id, newClassroom);
    setNewClassName('');
    setNewGroupLevel(6);
    setClassWarnings([]);
    setConfirmDuplicate(false);
  };
  // Enrich student data
  const enrichedStudents = useMemo(() => {
    return students.map(student => {
      let totalMastery = 0;
      let practicedCount = 0;
      let lowestMastery = 100;
      let highestMastery = 0;
      let weakestModule = null;
      let strongestModule = null;
      MODULES.forEach(module => {
        const mastery = getModuleMastery(student.id, module.id);
        if (mastery > 0) {
          totalMastery += mastery;
          practicedCount++;
          if (mastery < lowestMastery) {
            lowestMastery = mastery;
            weakestModule = module;
          }
          if (mastery > highestMastery) {
            highestMastery = mastery;
            strongestModule = module;
          }
        }
      });
      const avgMastery = practicedCount > 0 ? Math.round(totalMastery / practicedCount) : 0;
      const weeklyActivity = getWeeklyActivityForLearner(student.data.questionStates || {});
      const daysPracticed = weeklyActivity.filter(d => d.practiced).length;
      const trendDelta = getLearnerTrendDelta(student.data.familyMastery || {}, selectedGroup);
      return {
        ...student,
        avgMastery,
        lowestMastery,
        weakestModule,
        strongestModule,
        weeklyActivity,
        daysPracticed,
        trendDelta,
        needsAttention: needsAttention(lowestMastery, practicedCount),
        isGood: avgMastery >= 70,
        isInactive: daysPracticed === 0
      };
    });
  }, [students, getModuleMastery, selectedGroup]);
  // Filter and sort
  const filteredAndSortedStudents = useMemo(() => {
    let result = enrichedStudents;
    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(s => s.name.toLowerCase().includes(query));
    }
    // Filter
    switch (activeFilter) {
      case 'attention':
        result = result.filter(s => s.needsAttention);
        break;
      case 'good':
        result = result.filter(s => s.isGood);
        break;
      case 'inactive':
        result = result.filter(s => s.isInactive);
        break;
    }
    // Sort
    result.sort((a, b) => {
      switch (activeSort) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'mastery':
          return b.avgMastery - a.avgMastery;
        case 'activity':
          return b.daysPracticed - a.daysPracticed;
        default:
          return 0;
      }
    });
    return result;
  }, [enrichedStudents, searchQuery, activeFilter, activeSort]);
  // Get counts for filter tabs
  const counts = useMemo(() => {
    return {
      all: enrichedStudents.length,
      attention: enrichedStudents.filter(s => s.needsAttention).length,
      good: enrichedStudents.filter(s => s.isGood).length,
      inactive: enrichedStudents.filter(s => s.isInactive).length
    };
  }, [enrichedStudents]);
  const getMasteryColorClass = (mastery: number) => {
    if (mastery === 0) return 'text-slate-400';
    if (mastery >= 70) return 'text-emerald-600';
    if (mastery >= 40) return 'text-amber-600';
    return 'text-rose-600';
  };
  const isAllFilteredSelected = filteredAndSortedStudents.length > 0 && filteredAndSortedStudents.every(s => selectedIds.has(s.id));
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSelected = new Set(selectedIds);
    if (e.target.checked) {
      filteredAndSortedStudents.forEach(s => newSelected.add(s.id));
    } else {
      filteredAndSortedStudents.forEach(s => newSelected.delete(s.id));
    }
    setSelectedIds(newSelected);
  };
  const handleExportCSV = () => {
    const selectedStudents = enrichedStudents.filter(s => selectedIds.has(s.id));
    if (selectedStudents.length === 0) return;
    const headers = ['Naam', 'Beheersing (%)', 'Oefendagen', 'Trend (%)', 'Status'];
    const rows = selectedStudents.map(s => [s.name, s.avgMastery.toString(), s.daysPracticed.toString(), s.trendDelta.toString(), s.needsAttention ? 'Aandacht nodig' : s.isGood ? 'Goed bezig' : s.isInactive ? 'Inactief' : 'Bezig']);
    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    downloadCsv(csvContent, 'klas_export');
  };
  const summaryStats = useMemo(() => {
    if (enrichedStudents.length === 0) return null;
    const totalAvg = enrichedStudents.reduce((sum, s) => sum + s.avgMastery, 0);
    const classAvg = Math.round(totalAvg / enrichedStudents.length);
    let positiveTrend = 0;
    let negativeTrend = 0;
    enrichedStudents.forEach(s => {
      if (s.trendDelta > 0) positiveTrend++;else if (s.trendDelta < 0) negativeTrend++;
    });
    const attentionCount = enrichedStudents.filter(s => s.needsAttention).length;
    return {
      classAvg,
      positiveTrend,
      negativeTrend,
      attentionCount
    };
  }, [enrichedStudents]);
  const formatLastActive = (daysPracticed: number, weeklyActivity: DayActivity[]) => {
    if (daysPracticed === 0) return 'Nog niet';
    const recentDay = [...weeklyActivity].reverse().find(d => d.practiced);
    if (!recentDay) return 'Nog niet';
    return formatRelativeTime(new Date(recentDay.date).getTime());
  };
  return <PageLayout title="Mijn Klas" description={classroomName ? `Toont ${filteredAndSortedStudents.length} van ${students.length} leerlingen in ${classroomName}` : `Toont ${filteredAndSortedStudents.length} van ${students.length} leerlingen`} actions={classrooms.length > 0 && onSelectClassroom ? <ClassroomSwitcher classrooms={classrooms} selectedClassroomId={selectedClassroomId} onSelectClassroom={onSelectClassroom} activeTeacherId={activeAccount?.id} data-id="element-9685" /> : null} data-id="element-9684">
      <div className="max-w-5xl mx-auto space-y-6 pb-12" data-id="element-9686">
        <div className="flex justify-end mb-[-12px]" data-id="element-9687">
          <DataFreshness updatedAt={lastLoadedAt} onRefresh={() => setLastLoadedAt(new Date())} data-id="element-9688" />
        </div>

        {classrooms.length === 0 && <Card className="p-8 border-indigo-100 shadow-sm text-center bg-white max-w-2xl mx-auto mt-8" data-id="element-9689">
            <div className="flex justify-center mb-4" data-id="element-9690">
              <DjayNarrator size="md" emotion="excited" animate data-id="element-9691" />
            </div>
            <h2 className="text-2xl font-black text-slate-900 mb-3" data-id="element-9692">
              Maak je eerste klas aan
            </h2>
            <p className="text-slate-600 mb-8 max-w-md mx-auto" data-id="element-9693">
              Maak een klas aan om leerlingen toe te voegen en hun voortgang te
              volgen.
            </p>

            <div className="max-w-sm mx-auto text-left space-y-4" data-id="element-9694">
              <div data-id="element-9695">
                <label className="block text-sm font-bold text-slate-700 mb-1.5" data-id="element-9696">
                  Naam van de klas
                </label>
                <Input value={newClassName} onChange={e => {
              setNewClassName(e.target.value);
              setClassWarnings([]);
              setConfirmDuplicate(false);
            }} placeholder="Bijv. Groep 6A" className="w-full bg-slate-50 border-slate-200" data-id="element-9697" />
              </div>

              <div data-id="element-9698">
                <label className="block text-sm font-bold text-slate-700 mb-1.5" data-id="element-9699">
                  Standaard niveau
                </label>
                <Select value={newGroupLevel} onChange={e => setNewGroupLevel(Number(e.target.value))} options={[{
              value: 6,
              label: 'Groep 6'
            }, {
              value: 7,
              label: 'Groep 7'
            }, {
              value: 8,
              label: 'Groep 8'
            }]} className="w-full bg-slate-50 border-slate-200" data-id="element-9700" />
              </div>

              {classWarnings.length > 0 && <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex gap-3" data-id="element-9701">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-9702" />
                  <div data-id="element-9703">
                    <h4 className="font-bold text-amber-900 text-sm" data-id="element-9704">Let op</h4>
                    <ul className="mt-1 space-y-1" data-id="element-9705">
                      {classWarnings.map((w, i) => <li key={i} className="text-sm text-amber-800" data-id="element-9706">
                          {w.message}
                        </li>)}
                    </ul>
                    {!classWarnings.some(w => w.details.teacherId === activeAccount.id) && !confirmDuplicate && <button onClick={() => setConfirmDuplicate(true)} className="mt-2 text-sm font-bold text-amber-700 hover:text-amber-800 underline" data-id="element-9707">
                          Ja, ik wil deze naam toch gebruiken
                        </button>}
                  </div>
                </div>}

              <Button onClick={handleAddClassroom} disabled={!newClassName.trim()} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl mt-2" data-id="element-9708">
                <Plus className="w-4 h-4 mr-2" data-id="element-9709" />
                Klas Aanmaken
              </Button>
            </div>
          </Card>}

        {/* Summary Strip */}
        {classrooms.length > 0 && summaryStats && <Card className="bg-white border-slate-200 shadow-sm overflow-hidden" data-id="element-9710">
            <div className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-slate-100" data-id="element-9711">
              <div className="flex-1 p-4 flex items-center gap-4" data-id="element-9712">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-9713">
                  <CheckCircle2 className="w-5 h-5" data-id="element-9714" />
                </div>
                <div data-id="element-9715">
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-9716">
                    Gem. beheersing
                  </div>
                  <div className="text-2xl font-black text-slate-900" data-id="element-9717">
                    {summaryStats.classAvg}%
                  </div>
                </div>
              </div>
              <div className="flex-1 p-4 flex items-center gap-4" data-id="element-9718">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0" data-id="element-9719">
                  <ArrowUpRight className="w-5 h-5" data-id="element-9720" />
                </div>
                <div data-id="element-9721">
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-9722">
                    Trend
                  </div>
                  <div className="text-2xl font-black text-slate-900 flex items-baseline gap-2" data-id="element-9723">
                    <span className="text-emerald-600 flex items-center" data-id="element-9724">
                      {summaryStats.positiveTrend}{' '}
                      <ArrowUpRight className="w-4 h-4 ml-0.5" data-id="element-9725" />
                    </span>
                    <span className="text-rose-600 flex items-center text-lg" data-id="element-9726">
                      {summaryStats.negativeTrend}{' '}
                      <ArrowDownRight className="w-3 h-3 ml-0.5" data-id="element-9727" />
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex-1 p-4 flex items-center gap-4" data-id="element-9728">
                <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center flex-shrink-0" data-id="element-9729">
                  <AlertTriangle className="w-5 h-5" data-id="element-9730" />
                </div>
                <div data-id="element-9731">
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-9732">
                    Aandacht nodig
                  </div>
                  <div className="text-2xl font-black text-slate-900" data-id="element-9733">
                    {summaryStats.attentionCount}
                  </div>
                </div>
              </div>
            </div>
          </Card>}

        {/* Controls */}
        {classrooms.length > 0 && students.length > 0 && <div className="flex flex-col gap-4" data-id="element-9734">
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center" data-id="element-9735">
              <div className="flex flex-wrap items-center gap-2" data-id="element-9736">
                <FilterButton active={activeFilter === 'all'} onClick={() => setActiveFilter('all')} label="Alle" count={counts.all} color="indigo" data-id="element-9737" />
                <FilterButton active={activeFilter === 'attention'} onClick={() => setActiveFilter('attention')} label="Aandacht" count={counts.attention} color="rose" data-id="element-9738" />
                <FilterButton active={activeFilter === 'good'} onClick={() => setActiveFilter('good')} label="Goed bezig" count={counts.good} color="emerald" data-id="element-9739" />
                <FilterButton active={activeFilter === 'inactive'} onClick={() => setActiveFilter('inactive')} label="Inactief" count={counts.inactive} color="amber" data-id="element-9740" />
              </div>

              <div className="flex gap-3 w-full sm:w-auto items-center" data-id="element-9741">
                <div className="relative flex-1 sm:w-48" data-id="element-9742">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-9743" />
                  <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Zoeken..." className="pl-9 bg-white border-slate-200 rounded-xl w-full h-9 text-sm" data-id="element-9744" />
                </div>
                <Select value={activeSort} onChange={e => setActiveSort(e.target.value as SortType)} options={[{
              value: 'name',
              label: 'Naam'
            }, {
              value: 'mastery',
              label: 'Beheersing'
            }, {
              value: 'activity',
              label: 'Activiteit'
            }]} variant="admin" size="sm" fullWidth={false} className="bg-white border-slate-200 text-slate-700 font-medium rounded-xl h-9" data-id="element-9745" />
              </div>
            </div>
          </div>}

        {/* Compact Student Table */}
        {classrooms.length > 0 && (students.length === 0 ? <EmptyState title="Nog geen leerlingen" message="Voeg leerlingen toe aan deze klas om hun voortgang te volgen." emotion="thinking" colorScheme="indigo" actionLabel="Leerling Toevoegen" onAction={() => onNavigate?.('management')} data-id="element-9746" /> : filteredAndSortedStudents.length > 0 ? <Card className="border-slate-200 shadow-sm overflow-hidden bg-white relative" data-id="element-9747">
              {/* Table header */}
              <div className="grid grid-cols-[auto_auto_1fr_auto_auto_auto_auto] gap-4 items-center px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-9748">
                <div className="w-6 flex items-center justify-center" data-id="element-9749">
                  <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-600 cursor-pointer" checked={isAllFilteredSelected} onChange={handleSelectAll} data-id="element-9750" />
                </div>
                <div className="w-8" data-id="element-9751"></div>
                <div data-id="element-9752">Naam</div>
                <div className="hidden md:block w-32" data-id="element-9753">Zwakste vak</div>
                <div className="hidden md:block w-28" data-id="element-9754">Laatst actief</div>
                <div className="text-right w-24" data-id="element-9755">Beheersing</div>
                <div className="w-6" data-id="element-9756"></div>
              </div>

              {/* Student rows */}
              <div className="divide-y divide-slate-100" data-id="element-9757">
                {filteredAndSortedStudents.map((student, i) => {
            const statusColor = student.needsAttention ? 'bg-rose-500' : student.isGood ? 'bg-emerald-500' : student.isInactive ? 'bg-amber-500' : 'bg-indigo-400';
            const statusLabel = student.needsAttention ? 'Aandacht nodig' : student.isGood ? 'Goed bezig' : student.isInactive ? 'Inactief' : 'Bezig';
            return <div key={student.id} onClick={() => onSelectStudent(student.id)} className={`grid grid-cols-[auto_auto_1fr_auto_auto_auto_auto] gap-4 items-center px-4 py-2.5 cursor-pointer hover:bg-indigo-50/50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} data-id="element-9758">
                      {/* Checkbox */}
                      <div className="w-6 flex items-center justify-center" onClick={e => e.stopPropagation()} data-id="element-9759">
                        <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-600 cursor-pointer" checked={selectedIds.has(student.id)} onChange={e => {
                  const newSelected = new Set(selectedIds);
                  if (e.target.checked) {
                    newSelected.add(student.id);
                  } else {
                    newSelected.delete(student.id);
                  }
                  setSelectedIds(newSelected);
                }} data-id="element-9760" />
                      </div>

                      {/* Avatar */}
                      <div className="relative" data-id="element-9761">
                        <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarGradient(student.name)} flex items-center justify-center text-white font-bold text-sm`} data-id="element-9762">
                          {student.name.charAt(0).toUpperCase()}
                        </div>
                        <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${statusColor}`} role="img" aria-label={statusLabel} title={statusLabel} data-id="element-9763" />
                      </div>

                      {/* Name + coupling badges */}
                      <div className="min-w-0" data-id="element-9764">
                        <div className="flex items-center gap-2 flex-wrap" data-id="element-9765">
                          <div className="font-bold text-sm text-slate-900 truncate" data-id="element-9766">
                            {student.name}
                          </div>
                          {(() => {
                    const flags = getLearnerClassroomFlags(accounts, student.id, activeClassroom);
                    const learnerAcc = accounts.accountsById[student.id];
                    const learnerGrade = learnerAcc && learnerAcc.role === 'learner' ? learnerAcc.grade : undefined;
                    return <>
                                {flags.gradeMismatch && learnerGrade && <span onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded" title={`Werkt op groep-${learnerGrade}-niveau (deze klas is groep ${activeClassroom?.groupLevel}).`} data-id="element-9767">
                                    <GraduationCap className="w-3 h-3" data-id="element-9768" />
                                    Gr {learnerGrade}
                                  </span>}
                                {(flags.multiClassroom || flags.multiTeacher) && <span onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-sky-100 text-sky-700 text-[10px] font-bold rounded" title={flags.otherEnrollments.length > 0 ? 'Ook in: ' + flags.otherEnrollments.map(e => e.classroomName + ' (' + e.teacherName + ')').join(', ') : 'Gekoppeld aan meer dan een leerkracht'} data-id="element-9769">
                                    <Link2 className="w-3 h-3" data-id="element-9770" />
                                    {flags.otherEnrollments.length > 0 ? '+' + flags.otherEnrollments.length : 'Multi'}
                                  </span>}
                              </>;
                  })()}
                        </div>
                      </div>

                      {/* Weakest Subject */}
                      <div className="hidden md:block w-32" data-id="element-9771">
                        {student.weakestModule ? (() => {
                  const IconComp = getModuleIcon(student.weakestModule.icon);
                  return <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-100 border border-slate-200 text-xs font-medium text-slate-600 max-w-full hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition-colors cursor-pointer" onClick={e => {
                    e.stopPropagation();
                    onSelectStudent(student.id);
                  }} data-id="element-9772">
                                <IconComp className="w-3 h-3 flex-shrink-0" data-id="element-9773" />
                                <span className="truncate" data-id="element-9774">
                                  {student.weakestModule.title}
                                </span>
                              </div>;
                })() : <span className="text-slate-400 text-sm" data-id="element-9775">—</span>}
                      </div>

                      {/* Last Active */}
                      <div className="hidden md:block w-28 text-sm text-slate-500 font-medium" data-id="element-9776">
                        {formatLastActive(student.daysPracticed, student.weeklyActivity)}
                      </div>

                      {/* Mastery */}
                      <div className="flex items-center justify-end gap-1.5 w-24" data-id="element-9777">
                        <span className={`font-black text-sm ${getMasteryColorClass(student.avgMastery)}`} data-id="element-9778">
                          {student.avgMastery}%
                        </span>
                        {student.trendDelta !== 0 && <span className={student.trendDelta > 0 ? 'text-emerald-500' : 'text-rose-500'} title={`Trend: ${student.trendDelta > 0 ? '+' : ''}${student.trendDelta}%`} data-id="element-9779">
                            {student.trendDelta > 0 ? <ArrowUpRight className="w-3.5 h-3.5" data-id="element-9780" /> : <ArrowDownRight className="w-3.5 h-3.5" data-id="element-9781" />}
                          </span>}
                      </div>

                      {/* Arrow */}
                      <div className="text-slate-300 w-6 flex justify-end" data-id="element-9782">
                        <ArrowRight className="w-4 h-4" data-id="element-9783" />
                      </div>
                    </div>;
          })}
              </div>
            </Card> : <EmptyState title="Geen leerlingen gevonden" message="Probeer je zoekopdracht of filters aan te passen." emotion="thinking" colorScheme="slate" data-id="element-9784" />)}
      </div>

      {/* Floating Action Bar */}
      {selectedIds.size > 0 && <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-4 rounded-2xl shadow-xl flex items-center gap-6 z-50 animate-in slide-in-from-bottom-10 fade-in duration-300" data-id="element-9785">
          <div className="font-bold" data-id="element-9786">
            {selectedIds.size}{' '}
            {selectedIds.size === 1 ? 'leerling' : 'leerlingen'} geselecteerd
          </div>
          <div className="w-px h-6 bg-slate-700" data-id="element-9787"></div>
          <div className="flex items-center gap-3" data-id="element-9788">
            <button onClick={() => setSelectedIds(new Set())} className="text-sm font-medium text-slate-300 hover:text-white transition-colors" data-id="element-9789">
              Deselecteer alles
            </button>
            <Button onClick={() => onNavigate?.('reports')} size="sm" variant="secondary" className="bg-slate-800 hover:bg-slate-700 text-white border-slate-700" data-id="element-9790">
              <FileText className="w-4 h-4 mr-2" data-id="element-9791" />
              Bekijk Rapport
            </Button>
            <Button onClick={handleExportCSV} size="sm" className="bg-indigo-500 hover:bg-indigo-400 text-white border-none shadow-none" data-id="element-9792">
              <Download className="w-4 h-4 mr-2" data-id="element-9793" />
              Export CSV
            </Button>
          </div>
        </div>}
    </PageLayout>;
}
function FilterButton({
  active,
  onClick,
  label,
  count,
  color = 'indigo'
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  color?: 'indigo' | 'rose' | 'emerald' | 'amber';
}) {
  const colorClasses = {
    indigo: active ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200',
    rose: active ? 'bg-rose-600 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200',
    emerald: active ? 'bg-emerald-600 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200',
    amber: active ? 'bg-amber-500 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
  };
  const badgeClasses = {
    indigo: active ? 'bg-indigo-500 text-white' : 'bg-slate-100 text-slate-500',
    rose: active ? 'bg-rose-500 text-white' : 'bg-slate-100 text-slate-500',
    emerald: active ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500',
    amber: active ? 'bg-amber-400 text-white' : 'bg-slate-100 text-slate-500'
  };
  return <button onClick={onClick} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-bold transition-colors ${colorClasses[color]}`} data-id="element-9794">
      {label}
      <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-black leading-none ${badgeClasses[color]}`} data-id="element-9795">
        {count}
      </span>
    </button>;
}